/**
 * BusinessProfileForm — shared form used by Onboarding Step 1 and Settings.
 *
 * Refreshed 2026-05-22 redesign:
 *   - owner_phone field dropped (Step 4 WhatsApp pairing covers reachability)
 *   - website_url is OPTIONAL (no-website users can onboard)
 *   - Identity-first field order: Owner name → Business name → Email (RO) →
 *     Website URL → Timezone + Currency
 *   - Currency default derived from browser locale (was: phone country)
 *   - Card padding p-7 sm:p-9 to match the rest of the new onboarding flow
 *   - Optional legal footer under the submit button (for onboarding;
 *     Settings doesn't show it since the user already agreed at signup)
 *
 * See STATUS/PLAN_ONBOARDING_REDESIGN_PORT.md.
 */

import { useMemo, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Check, ChevronsUpDown } from 'lucide-react';
import { Link } from 'react-router-dom';

import { Banner } from '@/components/Banner';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { cn } from '@/lib/utils';

// --- URL helpers ---

export function normalizeUrl(raw: string): string {
  let s = raw.trim();
  if (!s) return '';
  if (!/^https?:\/\//i.test(s)) s = `https://${s}`;
  try {
    const u = new URL(s);
    u.hostname = u.hostname.toLowerCase();
    let out = u.toString();
    if (out.endsWith('/') && u.pathname === '/') out = out.slice(0, -1);
    return out;
  } catch {
    return s;
  }
}

function isHttpUrl(s: string): boolean {
  try {
    const u = new URL(s);
    return (u.protocol === 'http:' || u.protocol === 'https:') && !!u.hostname.includes('.');
  } catch {
    return false;
  }
}

function isValidWebsiteOrEmpty(v: string | undefined): boolean {
  if (!v || v.trim() === '') return true;
  return isHttpUrl(normalizeUrl(v));
}

// --- Validation ---

const schema = z.object({
  owner_name: z.string().min(2).max(120),
  business_name: z.string().min(2).max(120),
  website_url: z
    .string()
    .optional()
    .refine(isValidWebsiteOrEmpty, 'Please enter a valid website URL or leave blank'),
  timezone: z.string().min(1, 'Select a timezone'),
  currency: z.string().regex(/^[A-Z]{3}$/, 'Select a currency'),
});

export type BusinessProfileFormValues = z.infer<typeof schema>;

// --- Currency lists + locale-based default ---

const TOP_CURRENCIES = [
  'ILS', 'USD', 'EUR', 'GBP', 'CAD', 'AUD', 'JPY', 'CHF', 'NZD', 'SGD',
  'HKD', 'BRL', 'ZAR', 'MXN', 'INR', 'AED', 'SEK', 'NOK', 'DKK', 'PLN',
];

const REST_CURRENCIES = [
  'AFN', 'ALL', 'AMD', 'ANG', 'AOA', 'ARS', 'AWG', 'AZN', 'BAM', 'BBD',
  'BDT', 'BGN', 'BHD', 'BIF', 'BMD', 'BND', 'BOB', 'BSD', 'BTN', 'BWP',
  'BYN', 'BZD', 'CDF', 'CLP', 'CNY', 'COP', 'CRC', 'CUP', 'CVE', 'CZK',
  'DJF', 'DOP', 'DZD', 'EGP', 'ERN', 'ETB', 'FJD', 'FKP', 'GEL', 'GHS',
  'GIP', 'GMD', 'GNF', 'GTQ', 'GYD', 'HNL', 'HRK', 'HTG', 'HUF', 'IDR',
  'IQD', 'IRR', 'ISK', 'JMD', 'JOD', 'KES', 'KGS', 'KHR', 'KMF', 'KPW',
  'KRW', 'KWD', 'KYD', 'KZT', 'LAK', 'LBP', 'LKR', 'LRD', 'LSL', 'LYD',
  'MAD', 'MDL', 'MGA', 'MKD', 'MMK', 'MNT', 'MOP', 'MRU', 'MUR', 'MVR',
  'MWK', 'MYR', 'MZN', 'NAD', 'NGN', 'NIO', 'NPR', 'OMR', 'PAB', 'PEN',
  'PGK', 'PHP', 'PKR', 'PYG', 'QAR', 'RON', 'RSD', 'RUB', 'RWF', 'SAR',
  'SBD', 'SCR', 'SDG', 'SHP', 'SLL', 'SOS', 'SRD', 'STN', 'SVC', 'SYP',
  'SZL', 'THB', 'TJS', 'TMT', 'TND', 'TOP', 'TRY', 'TTD', 'TWD', 'TZS',
  'UAH', 'UGX', 'UYU', 'UZS', 'VES', 'VND', 'VUV', 'WST', 'XAF', 'XCD',
  'XOF', 'XPF', 'YER', 'ZMW',
];

// Browser-locale → ISO currency (replaces the old phone-country mapping
// since we no longer collect a phone number in Step 1).
const REGION_TO_CURRENCY: Record<string, string> = {
  US: 'USD', IL: 'ILS', GB: 'GBP', CA: 'CAD', AU: 'AUD',
  NZ: 'NZD', SG: 'SGD', HK: 'HKD', JP: 'JPY', CH: 'CHF',
  BR: 'BRL', ZA: 'ZAR', MX: 'MXN', IN: 'INR', AE: 'AED',
  SE: 'SEK', NO: 'NOK', DK: 'DKK', PL: 'PLN',
  DE: 'EUR', FR: 'EUR', IT: 'EUR', ES: 'EUR', NL: 'EUR',
  BE: 'EUR', AT: 'EUR', IE: 'EUR', PT: 'EUR', FI: 'EUR', GR: 'EUR',
};

function currencyForLocale(): string {
  try {
    const locale = navigator.language || 'en-US';
    // 'en-US' → 'US', 'de-DE' → 'DE', etc.
    const region = locale.split('-')[1]?.toUpperCase() ?? '';
    return REGION_TO_CURRENCY[region] ?? 'USD';
  } catch {
    return 'USD';
  }
}

// --- Timezone list (browser-native) ---

function getAllTimezones(): string[] {
  // Intl.supportedValuesOf('timeZone') is Baseline since ES2022.
  const anyIntl = Intl as unknown as { supportedValuesOf?: (k: string) => string[] };
  if (typeof anyIntl.supportedValuesOf === 'function') {
    return anyIntl.supportedValuesOf('timeZone');
  }
  return ['Asia/Jerusalem', 'America/New_York', 'Europe/London', 'UTC'];
}

// --- ComboBox ---

type ComboGroup = { label: string; options: string[] };

function ComboBox({
  value,
  onChange,
  options,
  groups,
  placeholder,
  emptyLabel = 'No results',
}: {
  value: string;
  onChange: (v: string) => void;
  options?: string[];
  groups?: ComboGroup[];
  placeholder: string;
  emptyLabel?: string;
}) {
  const [open, setOpen] = useState(false);
  const renderItem = (opt: string) => (
    <CommandItem
      key={opt}
      value={opt}
      onSelect={(currentValue) => {
        onChange(currentValue === value ? '' : currentValue);
        setOpen(false);
      }}
    >
      <Check className={cn('mr-2 h-4 w-4', value === opt ? 'opacity-100' : 'opacity-0')} />
      {opt}
    </CommandItem>
  );
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between"
        >
          {value || <span className="text-muted-foreground">{placeholder}</span>}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0">
        <Command>
          <CommandInput placeholder={`Search ${placeholder.toLowerCase()}…`} />
          <CommandList>
            <CommandEmpty>{emptyLabel}</CommandEmpty>
            {options && <CommandGroup>{options.map(renderItem)}</CommandGroup>}
            {groups &&
              groups.map((g) => (
                <CommandGroup key={g.label} heading={g.label}>
                  {g.options.map(renderItem)}
                </CommandGroup>
              ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function Field({
  label,
  error,
  children,
  hint,
}: {
  label: string;
  error?: string;
  children: React.ReactNode;
  hint?: string;
}) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      {children}
      {hint && !error && <p className="text-xs text-muted-foreground">{hint}</p>}
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
}

// --- Form ---

interface BusinessProfileFormProps {
  initialValues: Partial<BusinessProfileFormValues>;
  /** The customer's email shown read-only. Comes from `me.email`. */
  emailReadOnly?: string;
  /** Submit handler. Receives validated form values; resolves on success, throws on error. */
  onSubmit: (values: BusinessProfileFormValues) => Promise<void>;
  /** Label for the submit button. Defaults to "Save". */
  submitLabel?: string;
  /** Submitting label shown while onSubmit is in flight. */
  submittingLabel?: string;
  /** Settings shows a Discard button next to Save; Onboarding doesn't. */
  showDiscardButton?: boolean;
  /** Onboarding shows a "By continuing you agree to Terms + Privacy" footer; Settings doesn't. */
  showLegalFooter?: boolean;
}

export function BusinessProfileForm({
  initialValues,
  emailReadOnly,
  onSubmit,
  submitLabel = 'Save',
  submittingLabel = 'saving...',
  showDiscardButton = false,
  showLegalFooter = false,
}: BusinessProfileFormProps) {
  const [submitError, setSubmitError] = useState<string | null>(null);

  const browserTz =
    Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  const timezones = useMemo(getAllTimezones, []);
  const defaultCurrency = useMemo(currencyForLocale, []);

  const {
    control,
    register,
    handleSubmit,
    setValue,
    reset,
    formState: { errors, isSubmitting, isDirty },
  } = useForm<BusinessProfileFormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      business_name: '',
      owner_name: '',
      website_url: '',
      timezone: browserTz,
      currency: defaultCurrency,
      ...initialValues,
    },
  });

  const handleFormSubmit = async (values: BusinessProfileFormValues) => {
    setSubmitError(null);
    // Normalize website URL (trim, add https://, lowercase host). Empty → undefined.
    const normalizedWebsite = values.website_url ? normalizeUrl(values.website_url) : undefined;
    const submitted: BusinessProfileFormValues = {
      ...values,
      website_url: normalizedWebsite,
    };
    try {
      await onSubmit(submitted);
      reset(submitted);
    } catch (e) {
      setSubmitError(e instanceof Error ? e.message : String(e));
    }
  };

  return (
    <form
      onSubmit={handleSubmit(handleFormSubmit)}
      className="space-y-5 rounded-3xl border bg-card p-7 sm:p-9 shadow-card"
    >
      <Field label="Your name" error={errors.owner_name?.message}>
        <Input type="text" {...register('owner_name')} />
      </Field>

      <Field label="Business name" error={errors.business_name?.message}>
        <Input type="text" {...register('business_name')} />
      </Field>

      {emailReadOnly !== undefined && (
        <Field label="Email">
          <Input type="email" disabled value={emailReadOnly} />
        </Field>
      )}

      <Field
        label="Website URL"
        error={errors.website_url?.message}
        hint="Optional. We'll use this to detect your platform on the next step."
      >
        <Input
          type="text"
          placeholder="https://yourbusiness.com"
          {...register('website_url', {
            onBlur: (e) => {
              const v = e.target.value;
              if (v) setValue('website_url', normalizeUrl(v), { shouldValidate: true });
            },
          })}
        />
      </Field>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Timezone" error={errors.timezone?.message}>
          <Controller
            control={control}
            name="timezone"
            render={({ field }) => (
              <ComboBox
                value={field.value}
                onChange={field.onChange}
                options={timezones}
                placeholder="Select timezone"
              />
            )}
          />
        </Field>

        <Field label="Currency" error={errors.currency?.message}>
          <Controller
            control={control}
            name="currency"
            render={({ field }) => (
              <ComboBox
                value={field.value}
                onChange={field.onChange}
                groups={[
                  { label: 'Common', options: TOP_CURRENCIES },
                  { label: 'All currencies', options: REST_CURRENCIES },
                ]}
                placeholder="Select currency"
              />
            )}
          />
        </Field>
      </div>

      {submitError && <Banner variant="error">{submitError}</Banner>}

      {showDiscardButton ? (
        <div className="flex gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={() => reset()}
            disabled={!isDirty || isSubmitting}
            className="flex-1"
          >
            Discard
          </Button>
          <Button type="submit" disabled={!isDirty || isSubmitting} className="flex-1">
            {isSubmitting ? submittingLabel : submitLabel}
          </Button>
        </div>
      ) : (
        <Button
          type="submit"
          disabled={isSubmitting}
          className="w-full rounded-full bg-brand text-white hover:bg-brand-dark py-3.5 text-base font-bold"
        >
          {isSubmitting ? submittingLabel : submitLabel}
        </Button>
      )}

      {showLegalFooter && (
        <p className="text-center text-xs text-muted-foreground">
          By continuing you agree to our{' '}
          <Link
            to="/terms"
            className="underline underline-offset-2 hover:no-underline"
          >
            Terms
          </Link>{' '}
          and{' '}
          <Link
            to="/privacy"
            className="underline underline-offset-2 hover:no-underline"
          >
            Privacy Policy
          </Link>
          .
        </p>
      )}
    </form>
  );
}
